import { apiGet, apiPost } from './client.js';

// ─────────────────────────────────────────────────────────────
// System enum + display names
// ─────────────────────────────────────────────────────────────

export type AppAccessSystem = 'google_workspace' | 'github' | 'aws' | 'slack' | '1password' | 'claude';

export const SYSTEMS: AppAccessSystem[] = [
  'google_workspace',
  'github',
  'aws',
  'slack',
  '1password',
  'claude',
];

export const SYSTEM_DISPLAY_NAMES: Record<AppAccessSystem, string> = {
  google_workspace: 'Google Workspace',
  github: 'GitHub',
  aws: 'AWS',
  slack: 'Slack',
  '1password': '1Password',
  claude: 'Claude',
};

// Tier options for Claude access grants (JIT onboarding choice).
export type AppAccessTier = 'claude-standard' | 'claude-premium';

// Auto-managed systems are provisioned outside Monksheets (Slack via SSO,
// 1Password via SCIM): no manual dispatch, no bulk checkbox, always granted.
export const AUTO_MANAGED_SYSTEMS: AppAccessSystem[] = ['slack', '1password'];

export function isAutoManagedSystem(system: AppAccessSystem): boolean {
  return AUTO_MANAGED_SYSTEMS.includes(system);
}

// Bulk-excluded systems cannot be selected in the bulk-action checkboxes.
// Auto-managed systems are excluded because they need no manual dispatch.
// Claude is excluded because grant needs per-cell tier choice and revoke is manual confirmation.
export function isBulkExcludedSystem(system: AppAccessSystem): boolean {
  return isAutoManagedSystem(system) || system === 'claude';
}

// ─────────────────────────────────────────────────────────────
// Status enum + cell shape (matches Phase 7 D-01 + Phase 8 D-10)
// ─────────────────────────────────────────────────────────────

export type AppAccessStatus =
  | 'granted'
  | 'assumed_granted'
  | 'not_granted'
  | 'pending'
  | 'failed';

export type AppAccessSource = 'auto' | 'scim' | 'manual';

export interface MatrixCell {
  status: AppAccessStatus;
  source: AppAccessSource;
  lastDispatchedAt: string | null; // ISO date-time (research Pitfall #10: this is the cell's "last changed" surrogate)
  lastChangedBy: number | null; // FK userId
  lastFailureReason: string | null;
  tier: string | null; // Claude tier — 'claude-standard' | 'claude-premium' | null
}

export interface MatrixUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  deletedAt: string | null;
}

export interface MatrixRow {
  user: MatrixUser;
  cells: Record<AppAccessSystem, MatrixCell>;
}

export interface MatrixPagination {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface MatrixResponse {
  data: MatrixRow[];
  pagination: MatrixPagination;
}

// ─────────────────────────────────────────────────────────────
// History (per-cell timeline; Phase 8 D-08)
// ─────────────────────────────────────────────────────────────

export interface HistoryRun {
  id: number;
  type: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  triggeredBy: number | null; // userId — name lookup happens in the drawer (research A2)
  n8nExecutionUrl: string | null;
  appAccessSystem: string | null;
  appAccessDirection: string | null; // 'grant' | 'revoke' | 'retry' | null
  bulkOperationId: string | null; // BULK-04: shared id when run was part of a bulk batch; null otherwise.
  steps: { id: number; externalId: string; name: string; status: string }[];
}

// ─────────────────────────────────────────────────────────────
// Bulk dispatch types (Phase 10 D-13)
// ─────────────────────────────────────────────────────────────

export interface BulkPair {
  userId: number;
  system: AppAccessSystem;
}

export type BulkSkipReason = 'auto_managed' | 'ineligible_status' | 'pending' | 'missing_cell';

export interface BulkSkippedItem {
  userId: number;
  system: AppAccessSystem;
  reason: BulkSkipReason;
}

export interface BulkErrorItem {
  userId: number;
  system: AppAccessSystem;
  error: string;
}

export interface BulkDispatchResponse {
  bulkOperationId: string;
  dispatched: number;
  skipped: BulkSkippedItem[];
  errors: BulkErrorItem[];
}

// ─────────────────────────────────────────────────────────────
// Endpoint functions
// ─────────────────────────────────────────────────────────────

export interface GetMatrixParams {
  search?: string;
  status?: 'all' | AppAccessStatus;
  page?: number;
  pageSize?: number;
}

export async function getMatrix(params: GetMatrixParams = {}): Promise<MatrixResponse> {
  const qs = new URLSearchParams();
  if (params.search && params.search.length > 0) qs.set('search', params.search); // D-08: omit empty
  if (params.status && params.status !== 'all') qs.set('status', params.status); // D-08: omit default
  if (params.page && params.page > 1) qs.set('page', String(params.page)); // omit default page=1
  qs.set('pageSize', String(params.pageSize ?? 50)); // D-09: pageSize always sent (default 50)
  return apiGet<MatrixResponse>(`/admin/app-access/matrix?${qs.toString()}`);
}

export async function getCellHistory(
  userId: number,
  system: AppAccessSystem
): Promise<HistoryRun[]> {
  const res = await apiGet<{ data: HistoryRun[] }>(
    `/admin/users/${userId}/access/${system}/history`
  );
  return res.data;
}

export async function grantAccess(
  userId: number,
  system: AppAccessSystem,
  tier?: AppAccessTier
): Promise<{ runId: number; status: string }> {
  const body = tier ? { tier } : undefined;
  const res = await apiPost<{ data: { runId: number; status: string } }>(
    `/admin/users/${userId}/access/${system}/grant`,
    body
  );
  return res.data;
}

export async function revokeAccess(
  userId: number,
  system: AppAccessSystem,
  confirmation: string
): Promise<{ runId: number; status: string }> {
  const res = await apiPost<{ data: { runId: number; status: string } }>(
    `/admin/users/${userId}/access/${system}/revoke`,
    { confirmation }
  );
  return res.data;
}

export async function retryAccess(
  userId: number,
  system: AppAccessSystem
): Promise<{ runId: number; status: string; mode: 'resume' | 'fresh' }> {
  const res = await apiPost<{ data: { runId: number; status: string; mode: 'resume' | 'fresh' } }>(
    `/admin/users/${userId}/access/${system}/retry`
  );
  return res.data;
}

// ─────────────────────────────────────────────────────────────
// Bulk dispatch endpoints (Phase 10)
// ─────────────────────────────────────────────────────────────

export async function bulkGrant(pairs: BulkPair[]): Promise<BulkDispatchResponse> {
  const res = await apiPost<{ data: BulkDispatchResponse }>(
    '/admin/app-access/bulk-grant',
    { pairs }
  );
  return res.data;
}

export async function bulkRevoke(pairs: BulkPair[]): Promise<BulkDispatchResponse> {
  const res = await apiPost<{ data: BulkDispatchResponse }>(
    '/admin/app-access/bulk-revoke',
    { pairs }
  );
  return res.data;
}
